import { Button, Dropdown, Flex, FormCheckbox, Header, Input, ProviderConsumer, Text } from '@fluentui/react-northstar';
import { Communication } from '@fluentui/react-teams';
import { Providers } from '@microsoft/mgt-element';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useHistory } from "react-router-dom";
import '../AppGlobals';
import { MobileSettingsView } from '../automation/AutomationTable';
import ConfigCSS from '../config/config.module.css';
import MgtCss from '../mgt.module.css';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { ThemeColorScheme } from '../shared/common/TeamsTheme';
import { Breadcrumbs } from "../shared/components/BreadcrumbNavigation";
import { CheckLogin } from '../shared/components/CheckLogin';
import { LoadingScreen } from '../shared/components/LoadingScreen';
import { Toolbar } from '../shared/components/Toolbar';
import { UpgradeNeeded } from '../shared/components/UpgradeNeeded';
import { ICustomProperty, ICustomPropertyListConfiguration, ICustomPropertyListItem, ICustomPropertyListItemDto } from '../shared/interfaces/customproperties.interface';
import { platformService } from '../shared/services/platform.service';
import { CONTACT_ADMIN, FILTERS_STRINGS, LICENSE_FEATURE, REFRESH_SESSION, SAVE, SETTINGS, SETTINGS_PAGES, SOMETHING_WENT_WRONG, TEMPLATE, UNAUTHORIZED_ACCESS } from '../shared/utils/constants';
import { toTitleCase } from '../shared/utils/helper';
import { CustomPropertyListItem } from './CustomPropertyListItem';
import { CustomPropertyListItems } from './CustomPropertyListItems';
import { CustomPropertyDataType_LABELS, CustomPropertyListPresentation_LABELS } from './utils/constants';

class CustomProperty implements ICustomProperty {
    constructor(id, propertyId, name, description, dataType, isRequired, errorMessage, isHidden) {
        this.Id = id;
        this.PropertyId = propertyId;
        this.Name = name;
        this.Description = description;
        this.DataType = dataType;
        this.IsRequired = isRequired;
        this.ErrorMessage = errorMessage;
        this.IsHidden = isHidden
    }
    Id: 0;
    PropertyId: "";
    Name: "";
    Description: "";
    DataType: "";
    IsRequired: false;
    ErrorMessage: "";
    IsHidden: false;
}

export interface ICustomPropertyForm {
    id: string,
}

const LabelElement = (props: any) => {
        return (
            <div>
                <Flex styles={{ marginTop: '1rem' }}>
                    <Text content={props.label} style={{ display: 'block' }} />
                    {props.required && <span style={{ color: 'red', marginLeft: 2 }}>*</span>}
                </Flex>
                <div>{props.children}</div>
            </div>
        )
    }

export const CustomPropertyForm = (props: ICustomPropertyForm) => {
    const { t } = useTranslation();
    const history = useHistory();
    const api = new platformService();

    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [errCode, setErrCode] = useState(0);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [isNameInvalid, setIsNameInvalid] = useState(false);
    const [isPropertyIdInvalid, setIsPropertyIdInvalid] = useState(false);
    const [isPropertyIdContainsInvalid, setIsPropertyIdContainsInvalid] = useState(false);
    const [property, setProperty] = useState<ICustomProperty>(new CustomProperty(0, "", "", "", "STRING", false, "", false));
    const [propertyListConfig, setPropertyListConfig] = useState<ICustomPropertyListConfiguration>({Id: 0, AllowMultiple: false, TeamsPresentation: "LOOKUP"});
    const [propertyListItems, setPropertyListItems] = useState<ICustomPropertyListItem[]>([]);
    const [dataTypeDropDown, setDataTypeDropDown] = useState<DropdownDataModel[]>([]);
    const [dataListPresentationDropDown, setListPresentationDropDown] = useState<DropdownDataModel[]>([]);
    const [isFeatureEnabled, setIsFeatureEnabled] = useState(true);

    const getDropdownItems = (keys) => {
        const dropdownItems = [];
        Object.keys(keys).forEach(label => {
            dropdownItems.push({
                key: label,
                label: keys[label],
                header: keys[label],
                value: label
            } as DropdownDataModel);
        });   
        return dropdownItems;  
    }

    const fetchData = async () => {
        setLoadingMessage(t('custom-properties.get-item'));
        setIsFeatureEnabled(await getCachedFeature(LICENSE_FEATURE.CustomProperties));
        setDataTypeDropDown(getDropdownItems(CustomPropertyDataType_LABELS));
        setListPresentationDropDown(getDropdownItems(CustomPropertyListPresentation_LABELS));
        if (props.id && parseInt(props.id) > 0) {
            const result = await api.getCustomProperties(`(${props.id})?${FILTERS_STRINGS.CUSTOM_PROPERTY_LIST}`).then((val) => {
                let value = val.data;
                setIsError(false);
                return value;
            })
            .catch((e: any) => {
                setIsError(true);
                setErrCode(e.response.status);
            })
            .finally(() => {
                setIsLoading(false);
            });

            if(result.hasOwnProperty("Id")){
                setProperty(result);
                if(result.CustomPropertyListConfiguration)
                    setPropertyListConfig(result.CustomPropertyListConfiguration);
                if(result.CustomPropertyListItems)
                    setPropertyListItems(result.CustomPropertyListItems);
            }
        } else
            setIsLoading(false);
    }

    const loadPage = () => {
        fetchData();
    }

    const checkNameOrIdExist = async (property: ICustomProperty) => {
        const encodedPropertyId = encodeURIComponent(property.PropertyId.replace(/'/ig, "''"));
        const encodedName = encodeURIComponent(property.Name.replace(/'/ig, "''"));
        const { data: { value } } = await api.getCustomProperties(`?$filter=Name eq '${encodedName}' OR PropertyId eq '${encodedPropertyId}'`);

        const result: ICustomProperty[] = value || [];

        const sameName = result.some(x => x.Name == property.Name && x.Id !== property.Id);
        const samePropertyId = result.some(x => x.PropertyId == property.PropertyId && x.Id !== property.Id);
        setIsNameInvalid(sameName);
        setIsPropertyIdInvalid(samePropertyId);
        return sameName || samePropertyId;
    }

    const saveData = async () => {

        if(await checkNameOrIdExist(property)) return;
        if(/[^a-zA-Z\d]|^\d/.test(property.PropertyId)){
            setIsPropertyIdContainsInvalid(true);
            return;
        }

        setIsNameInvalid(false);
        setIsPropertyIdInvalid(false);
        setIsPropertyIdContainsInvalid(false);

        setLoadingMessage(t('custom-properties.saving-data'));
        setIsLoading(true);
        if (property.Id === 0){
            const count = await api.getCustomProperties(`/$count`);
            property.Order = count.data;
            const result = await api.createCustomProperty(property);
            property.Id = result?.data?.Id ?? 0;
        }
        else{
            delete property.CustomPropertyListConfiguration;
            delete property.CustomPropertyListItems;
            await api.updateCustomProperty(property);
        }

        if(property.Id > 0 && CustomPropertyDataType_LABELS[property.DataType] === CustomPropertyDataType_LABELS.LIST){
            const propertyListItemsDto = propertyListItems.map(p => {
                return {
                    Id: p.Id,
                    Value: p.Value,
                    Position: p.Position,
                    ParentId: p.ParentId,
                    IsDefault: p.IsDefault,
                    IsArchived: p.IsArchived,
                    IsDeleted: p.IsDeleted
                } as ICustomPropertyListItemDto;
            });
            await api.updateCustomPropertyList(property.Id, propertyListItemsDto, propertyListConfig);
        }

        setIsLoading(false);
        history.push(`/settings/custom-properties`)
    };

    const errConfig = {
        fields: {
            title: (errCode == 401) ? UNAUTHORIZED_ACCESS : SOMETHING_WENT_WRONG,
            desc: CONTACT_ADMIN
        }
    };

    const navs: Breadcrumbs = {
        breadcrumbs: [
            {
                title: t('common.breadcrumb.settings'),
                link: '/settings'
            },
            {
                title: t('custom-properties.menu-title'),
                link: '/settings/custom-properties'
            },
            {
                title: (property.Name.length > 0) ? property.Name : t('custom-properties.new-title'),
                link: ""
            }
        ]
    };

    return (
        <CheckLogin onSignedIn={loadPage}>
            <Helmet>
                <title>{((property.Name?.length ?? 0) > 0) ? property.Name : t('custom-properties.new-title')} - Tikit</title>
            </Helmet>
            {(!isFeatureEnabled) ? <UpgradeNeeded
                  toolbarText={`${toTitleCase(SETTINGS)} > ${toTitleCase(SETTINGS_PAGES.CUSTOM_PROPERTIES)}`}
                  headerText={t('custom-properties.no-access-current-plan')}
                  subheaderText={t('custom-properties.no-access-upgrade-plan')}/> : 
                <>
                <div className="hidden md:block">
                    {
                        (isLoading) ? <LoadingScreen message={loadingMessage} /> :
                            <>
                            {isError && (<>
                                <Communication {...errConfig} />
                                {errCode === 401 && (<Button content={REFRESH_SESSION} primary onClick={e => { Providers.globalProvider.login(); }} />)}
                            </>)}
                            {!isError && (
                                <ProviderConsumer render={(globalTheme) => (
                                <div style={{
                                    display: 'block',
                                    ...{ '--input-background-color': 'var(--mgt-theme-background)' },
                                    ...ThemeColorScheme(globalTheme.siteVariables)
                                }}>
                                    <Toolbar 
                                    globalTheme={globalTheme} title={`${toTitleCase(SETTINGS)} > ${t('custom-properties.page-title')}`}
                                    saveText={SAVE} 
                                    saveCommand={saveData} 
                                    breadcrumbs={navs}
                                    disableSave={!property.PropertyId || !property.Name || (property.IsRequired && !property.ErrorMessage)} 
                                    />
                                    <Flex className={ConfigCSS.configBody}
                                        styles={{overflow: "auto"}}>
                                        <Flex.Item>
                                            <Flex column
                                                className={`pr-3 lg:w-1/2 w-full`}
                                                styles={{
                                                    // overflow: "auto",
                                                    paddingLeft: "15px",
                                                }}>
                                                <Header content={t('custom-properties.info-section')} className="mb-2" />
                                                <LabelElement label={t('custom-properties.fields.id')} required={true}></LabelElement>
                                                <div className="teams-input-med">
                                                    <Input
                                                        clearable
                                                        inverted
                                                        disabled={property.Id > 0}
                                                        value={property.PropertyId}
                                                        onChange={(_, v) => setProperty({...property, PropertyId: v.value})}
                                                        fluid />
                                                        {!isPropertyIdInvalid && isPropertyIdContainsInvalid && (
                                                            <div style={{ color: '#c4314b', marginBottom: '1em' }}>
                                                            {!property.PropertyId || (property?.PropertyId.length ?? 0) == 0
                                                                ? TEMPLATE.ERROR_NAME_REQUIRED
                                                                : t("common.id-input-error")}
                                                            </div>
                                                        )}
                                                        {isPropertyIdInvalid && (
                                                            <div style={{ color: '#c4314b', marginBottom: '1em' }}>
                                                            {!property.PropertyId || (property?.PropertyId.length ?? 0) == 0
                                                                ? TEMPLATE.ERROR_NAME_REQUIRED
                                                                : TEMPLATE.ERROR_NAME_EXIST}
                                                            </div>)}
                                                </div>
                                                <LabelElement label={t('custom-properties.fields.name')} required={true}></LabelElement>
                                                <div className="teams-input-med">
                                                    <Input
                                                        clearable
                                                        inverted
                                                        value={property.Name}
                                                        onChange={(_, v) => setProperty({...property, Name: v.value})}
                                                        fluid />
                                                        {isNameInvalid && (
                                                            <div style={{ color: '#c4314b', marginBottom: '1em' }}>
                                                            {!property.Name || (property.Name?.length ?? 0) == 0
                                                                ? TEMPLATE.ERROR_NAME_REQUIRED
                                                                : TEMPLATE.ERROR_NAME_EXIST}
                                                            </div>
                                                        )}
                                                </div>
                                                <LabelElement label={t('custom-properties.fields.description')}></LabelElement>
                                                <div className="teams-input-med">
                                                    <Input
                                                        clearable
                                                        inverted
                                                        value={property.Description}
                                                        onChange={(_, v) => setProperty({...property, Description: v.value})}
                                                        fluid />
                                                </div>
                                                <LabelElement label={t('custom-properties.fields.data-type')}></LabelElement>
                                                <Dropdown
                                                    inverted
                                                    disabled={property.Id > 0}
                                                    fluid
                                                    items={dataTypeDropDown}
                                                    value={dataTypeDropDown.filter(d => d.value == property.DataType)}
                                                    onChange={(e, p) => {
                                                        const item = p.value as DropdownDataModel;
                                                        setProperty({...property, DataType: item.value})
                                                    }}
                                                    style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background }}
                                                />
                                                <Flex
                                                    className="teams-input-med lg:w-1/2  mt-4"
                                                    styles={{ width: 'fit-content', }}
                                                    >
                                                    <FormCheckbox
                                                        label={t('custom-properties.fields.is-required')}
                                                        checked={property.IsRequired}
                                                        onChange={(_, v) => setProperty({...property, IsRequired: v.checked})}
                                                    />
                                                </Flex>

                                                <div className={property.IsRequired ? '' : MgtCss.disabledContainer}>
                                                    <LabelElement label={t('custom-properties.fields.error-message')} required={true}></LabelElement>
                                                    <div className="teams-input-med">
                                                        <Input
                                                            disabled={!property.IsRequired}
                                                            clearable
                                                            inverted
                                                            value={property.ErrorMessage}
                                                            onChange={(_, v) => setProperty({...property, ErrorMessage: v.value})}
                                                            fluid />
                                                    </div>
                                                </div>

                                                <Flex
                                                    className="teams-input-med lg:w-1/2  mt-4"
                                                    styles={{ width: 'fit-content', }}
                                                    >
                                                    <FormCheckbox
                                                        label={t('custom-properties.fields.is-hidden')}
                                                        checked={property.IsHidden}
                                                        onChange={(_, v) => setProperty({...property, IsHidden: v.checked})}
                                                    />
                                                </Flex>

                                                {CustomPropertyDataType_LABELS[property.DataType] === CustomPropertyDataType_LABELS.LIST && (
                                                <>
                                                    <Header content={t('custom-properties.list-config')} className="mb-2" />
                                                    <Flex
                                                        className="teams-input-med lg:w-1/2 mt-4"
                                                        styles={{ width: 'fit-content', }}
                                                        >
                                                        <FormCheckbox
                                                            label={t('custom-properties.fields.allow-multiple')}
                                                            checked={propertyListConfig.AllowMultiple}
                                                            onChange={(_, v) => setPropertyListConfig({...propertyListConfig, AllowMultiple: v.checked})}
                                                        />
                                                    </Flex>
                                                    <div className="italic text-xs" style={{ color: globalTheme.siteVariables.colorScheme.default.foreground2 }}>{t('custom-properties.fields.allow-multiple-hint')}</div>
                                                    <LabelElement label={t('custom-properties.fields.teams-presentation')}></LabelElement>
                                                    <div className="italic text-xs pb-2" style={{ color: globalTheme.siteVariables.colorScheme.default.foreground2 }}>{t('custom-properties.fields.teams-presentation-hint')}</div>
                                                    <Dropdown
                                                        inverted
                                                        fluid
                                                        items={dataListPresentationDropDown}
                                                        value={dataListPresentationDropDown.filter(d => d.value == propertyListConfig.TeamsPresentation)}
                                                        onChange={(e, p) => {
                                                            const item = p.value as DropdownDataModel;
                                                            setPropertyListConfig({...propertyListConfig, TeamsPresentation: item.value})
                                                        }}
                                                        style={{ backgroundColor: globalTheme.siteVariables.colorScheme.default.background }}
                                                    />
                                                    {true
                                                    ? <CustomPropertyListItem property={property} propertyListItems={propertyListItems} setPropertyListItems={setPropertyListItems}/>
                                                    : <CustomPropertyListItems propertyId={property.Id}/>
                                                    }
                                                    </>
                                                )}
                                            </Flex>
                                        </Flex.Item>
                                    </Flex>
                                </div>)} />
                            )}
                            </>
                    }
                </div>
                <MobileSettingsView />
                </>
            }
        </CheckLogin>
    )
}